import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UsersPage } from '../display-models/user-page.model';
import { HttpService } from './http.service';
import { BaseService } from './base.service';
import { PaginationService } from './pagination.service';
import { UserDetailDto } from '../dtos/user-detail.dto';
import { UserListDto } from '../dtos/user-list.dto';
import { OrgChartNodeDto } from '../dtos/org-chart-node.dto';
import { UtilFunctions } from '../utils/funcs.util';
import { SheetImportReport, UserSheetRow } from '../models/sheet-transfer.model';
@Injectable({
  providedIn: 'root',
})
export class UserService extends BaseService<UserDetailDto> {
  paginationService = inject(PaginationService);

  fullName: string = '';
  public usersPageSubject: BehaviorSubject<UsersPage> = new BehaviorSubject(this.setUsersPageSubject);
  public isPagninationUsersRefrech: boolean = true;

  /** Shared state for the currently viewed profile (set by resolver, consumed by detail components). */
  private selectedProfileSubject = new BehaviorSubject<UserDetailDto | null>(null);
  public selectedProfile$ = this.selectedProfileSubject.asObservable();

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    const http = inject(HttpService);

    super(http, '/v1.0/users');
  }

  setSelectedProfile(profile: UserDetailDto | null): void {
    this.selectedProfileSubject.next(profile);
  }

  getSelectedProfileSnapshot(): UserDetailDto | null {
    return this.selectedProfileSubject.getValue();
  }

  public set setUsersPageSubject(value: UsersPage) {
    this.usersPageSubject.next(value);
  }

  clearUsersPage() {
    this.setUsersPageSubject = new UsersPage();
  }

  public addUserToPageSubject(value: UserListDto) {
    const page = this.usersPageSubject.value;
    page.content = [...page.content, value];
    this.isPagninationUsersRefrech = false;
    this.usersPageSubject.next(page);
    this.isPagninationUsersRefrech = true;
  }

  public removeUserFromPageSubject(value: UserListDto) {
    const page = this.usersPageSubject.value;
    UtilFunctions.deleteWithIdInTheList(value, page.content);
    this.isPagninationUsersRefrech = false;
    this.usersPageSubject.next(page);
    this.isPagninationUsersRefrech = true;
  }

  resetFilters() {
    this.fullName = '';
    this.paginationService.resetPageAndSize({ page: 0, size: 8 });
  }

  public retrieveUsersPage(fullName?: string): Promise<any> {
    const params: any = {
      page: this.paginationService.currentPage,
      size: this.paginationService.size,
    };
    if (fullName) params['fullName'] = fullName;
    return this.http.get('/v1.0/users/findUserTeamByFullName', params);
  }

  public getUsersForDropDownByKey(key?: string): Promise<UserDetailDto[] | undefined> {
    return this.http.get('/v1.0/users/getUsersForDropDown', { fullName: key ?? '' });
  }

  public retrieveUsersPageByProject(projectId: number) {
    const params: any = {
      projectId,
      page: this.paginationService.currentPage,
      size: this.paginationService.size,
    };
    this.http
      .get('/v1.0/users/getEmployeesByProjectId', params)
      .then((data: any) => {
        if (data) {
          this.setUsersPageSubject = data;
          this.paginationService.init({
            currentPageSize: data?.size ?? 0,
            totalElements: data?.totalElements ?? 0,
            totalPages: data?.totalPages ?? 0,
          });
        } else {
          this.setUsersPageSubject = new UsersPage();
          this.paginationService.init({});
        }
      })
      .catch(() => {
        console.error('Error fetching users by project');
      });
  }

  public findByUsername(username: string): Promise<any> {
    return this.http.get('/v1.0/users/findByUsername', { username });
  }

  public forgotPassword(data: any): Promise<any> {
    return this.http.post('/v1.0/users/forgotPassword', data, '', 'text');
  }

  public changePassword(data: any): Promise<any> {
    return this.http.post('/v1.0/users/updatePassword', data, '', 'text');
  }

  public updateUserStatus(userId: number, status: boolean): Promise<any> {
    let data = { id: userId, enabled: status };
    return this.http.patch(`/v1.0/users/status`, data);
  }

  public setNewPassword(data: any): Promise<any> {
    return this.http.post('/v1.0/users/setNewPassword', data, '', 'text');
  }

  public getEmployeesOfTheMonth(date?: String): Promise<any> {
    const params: any = {};
    if (date) params['date'] = date;
    return this.http.get('/v1.0/users/getEmployeesOfTheMonth', params);
  }

  public getTeamByResponsible(): Promise<any> {
    return this.http.get('/v1.0/users/findTeamByResponsible', {});
  }

  /**
   * `GET /v1.0/users/org-chart` — the chart around the signed-in user.
   *
   * Both bounds are optional and mean "as far as there is graph" when omitted, which is what
   * the page loads with. `up` roots the chart that many managers above the viewer (and so
   * also brings in their peers); `down` is counted from the viewer, not from that root.
   *
   * Returns a list, not a single node: somebody with two managers who each top out
   * separately has two roots, and the chart draws them side by side.
   */
  public getOrgChart(up?: number | null, down?: number | null): Promise<OrgChartNodeDto[]> {
    const params: Record<string, number> = {};
    if (up !== null && up !== undefined) params['up'] = up;
    if (down !== null && down !== undefined) params['down'] = down;
    return this.http
      .get<OrgChartNodeDto[]>('/v1.0/users/org-chart', params)
      .then((nodes) => nodes ?? []);
  }

  public getUsersWithoutParticipation(date: string, page: number, size: number): Promise<any> {
    return this.http.get('/v1.0/users/without-participation', { date, page, size });
  }

  /**
   * Creates one employee per sheet line, with what creating one by hand gives them. All or
   * nothing: a file with any problem writes nothing and comes back with every problem.
   */
  importSheetRows(rows: UserSheetRow[]): Promise<SheetImportReport> {
    return this.http.post<SheetImportReport>('/v1.0/users/import-rows', rows);
  }

  /** The employees this user can see, as sheet lines. */
  exportSheetRows(): Promise<UserSheetRow[]> {
    return this.http.get<UserSheetRow[]>('/v1.0/users/export-rows').then((rows) => rows ?? []);
  }

  /**
   * Whether no employee of the company uses this email, ignoring case. `excludeId` is the
   * employee being edited: their own address is free for them.
   *
   * Asked while the address is typed, so it never raises the error dialog: a check that
   * could not be made is the save's to report, not a keystroke's.
   */
  isEmailAvailable(email: string, excludeId?: number | null): Promise<boolean> {
    const params: Record<string, string | number> = { email };
    if (excludeId != null) params['excludeId'] = excludeId;
    return this.http.get<boolean>('/v1.0/users/email-available', params, '', { skipErrorAlert: true });
  }
}
